import { css } from '@emotion/react';
import React, { useCallback, useEffect, useState } from 'react';
import cx from 'classnames';
import { COLORS3 } from '../../styles/emotion';
import { LinkV2 } from '../LinkV2';

type FavoriteHeartColor = {
  outline: string;
  background: string;
};
type FavoriteHeartColorProps = {
  favoriteColor: FavoriteHeartColor;
  unfavoriteColor: FavoriteHeartColor;
};

/**
 * The CSS the colors the favorite heart SVG based on the visual state provided
 * which may be favorited or unfavorited.  Note, that on hover, we toggle the
 * visual state using mouseEnter and mouseLeave events.
 */
const FavoriteHeartWrapperCss = ({
  isVisuallyFavorited,
  favoriteColor,
  unfavoriteColor,
}: { isVisuallyFavorited: boolean } & FavoriteHeartColorProps) => {
  return css`
    display: inline;
    svg {
      path {
        transition: fill 0.3s ease-in-out;
        &:first-of-type {
          // Body of the heart
          ${isVisuallyFavorited
            ? `fill: ${favoriteColor.background}`
            : `fill: ${unfavoriteColor.background};`}
        }
        &:last-of-type {
          // border of the heart
          ${isVisuallyFavorited
            ? `fill: ${favoriteColor.outline}`
            : `fill: ${unfavoriteColor.outline};`}
        }
      }
    }
  `;
};

/**
 * A favorite heart icon with SVG paths that can be programmatically colored.
 *
 * Must be used within a component applying FavoriteHeartWrapperCss or the paths
 * used to draw the SVG will be transparent.
 *
 */
const FavoriteHeartWithColorablePaths = (props: React.SVGProps<SVGSVGElement>): JSX.Element => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.25 11.3099L9.99995 17.4999L3.74995 11.3099M3.74996 11.3099C2.62604 10.2163 2.2114 8.58414 2.67703 7.08663C3.14265 5.58912 4.40967 4.47989 5.9556 4.21635C7.50152 3.95282 9.06449 4.57961 9.99996 5.83827C10.9394 4.58893 12.499 3.97045 14.0393 4.23645C15.5796 4.50245 16.8415 5.60818 17.3075 7.10023C17.7734 8.59227 17.3651 10.2196 16.25 11.3149"
    />
    <path d="M16.8364 11.902C17.1634 11.5782 17.1659 11.0505 16.842 10.7235C16.5182 10.3965 15.9905 10.394 15.6635 10.7178L16.8364 11.902ZM9.99995 17.4999L9.41354 18.092C9.73832 18.4137 10.2616 18.4137 10.5864 18.092L9.99995 17.4999ZM4.33636 10.7178C4.00936 10.394 3.48172 10.3965 3.15786 10.7235C2.834 11.0505 2.83654 11.5782 3.16354 11.902L4.33636 10.7178ZM3.16879 11.9072C3.49864 12.2281 4.02623 12.2209 4.3472 11.8911C4.66817 11.5613 4.66097 11.0337 4.33113 10.7127L3.16879 11.9072ZM9.99996 5.83827L9.33113 6.33537C9.48787 6.54626 9.73489 6.67087 9.99764 6.6716C10.2604 6.67233 10.5081 6.5491 10.666 6.33908L9.99996 5.83827ZM15.666 10.7204C15.3377 11.0429 15.3329 11.5705 15.6554 11.8989C15.9779 12.2272 16.5056 12.232 16.8339 11.9095L15.666 10.7204ZM15.6635 10.7178L9.41354 16.9078L10.5864 18.092L16.8364 11.902L15.6635 10.7178ZM10.5864 16.9078L4.33636 10.7178L3.16354 11.902L9.41354 18.092L10.5864 16.9078ZM4.33113 10.7127C3.43199 9.83776 3.10028 8.53206 3.47278 7.33406L1.88127 6.8392C1.32252 8.63621 1.82009 10.5948 3.16879 11.9072L4.33113 10.7127ZM3.47278 7.33406C3.84528 6.13605 4.8589 5.24867 6.09564 5.03784L5.81556 3.39487C3.96045 3.71112 2.44003 5.0422 1.88127 6.8392L3.47278 7.33406ZM6.09564 5.03784C7.33238 4.82701 8.58275 5.32844 9.33113 6.33537L10.6688 5.34117C9.54622 3.83078 7.67067 3.07863 5.81556 3.39487L6.09564 5.03784ZM10.666 6.33908C11.4175 5.33961 12.6652 4.84483 13.8975 5.05763L14.1811 3.41527C12.3327 3.09607 10.4612 3.83824 9.33391 5.33746L10.666 6.33908ZM13.8975 5.05763C15.1297 5.27043 16.1392 6.15502 16.512 7.34865L18.1029 6.8518C17.5437 5.06135 16.0295 3.73448 14.1811 3.41527L13.8975 5.05763ZM16.512 7.34865C16.8848 8.54229 16.5581 9.84416 15.666 10.7204L16.8339 11.9095C18.1721 10.5951 18.6621 8.64226 18.1029 6.8518L16.512 7.34865Z" />
  </svg>
);

export type FavoriteHeartComponentType = (props: {
  /** Mouse event handler that toggles the visual state of the heart */
  onMouseEnter: React.MouseEventHandler;
  /** Mouse event handler that restores the visual state of the heart */
  onMouseLeave: React.MouseEventHandler;
  children: React.ReactNode;
  css: ReturnType<typeof css>;
  className?: string;
}) => JSX.Element;

type FavoriteHeartSvgProps = {
  height?: number;
  width?: number;
};

/**
 * A wrapper for the favorite heart SVG that applies a hover effect to the heart
 * and provides the default colors for the heart.
 *
 * You probably want to use FavoriteHeartButton for most cases, but this component
 * will allow you more control if needed.
 */
export const FavoriteHeart = ({
  component: FavoriteHeartComponent,
  isFavorited,
  height = 20,
  width = 20,
  favoriteColor = {
    outline: COLORS3.ROSE_100,
    background: COLORS3.BLUSH_100,
  },
  unfavoriteColor = {
    outline: COLORS3.BLACK_030,
    background: 'transparent',
  },
  className,
}: {
  /**
   * What gets rendered as the wrapper around the heart SVG.  This component
   * needs to handle mouseEnter, mouseLeave and apply className/css/children
   *
   * If you use FavoriteHeartButton this will be a LinkV2 rendered as a button
   * but you can use this to render a different component if needed.
   */
  component: FavoriteHeartComponentType;

  isFavorited: boolean;
  className?: string;
} & FavoriteHeartSvgProps &
  Partial<FavoriteHeartColorProps>) => {
  const [isVisuallyFavorited, setIsVisuallyFavorited] = useState(isFavorited);
  useEffect(() => {
    setIsVisuallyFavorited(isFavorited);
  }, [isFavorited]);

  const onMouseEnter: React.MouseEventHandler = useCallback(() => {
    // Set the visual state to what it will be if they click
    setIsVisuallyFavorited(!isFavorited);
  }, [isFavorited]);

  const onMouseLeave: React.MouseEventHandler = useCallback(() => {
    // Restore the visual state to what it was before mouse enter
    setIsVisuallyFavorited(isFavorited);
  }, [isFavorited]);

  return FavoriteHeartComponent({
    onMouseEnter,
    onMouseLeave,
    children: <FavoriteHeartWithColorablePaths height={height} width={width} />,
    className,
    css: FavoriteHeartWrapperCss({ isVisuallyFavorited, favoriteColor, unfavoriteColor }),
  });
};

/**
 * Helper for the common use case of presenting a favorite heart as an
 * actionable button with an onClick handler.
 *
 * - When the component is hovered, the heart will visually change to the opposite state
 * - Click handling is up to the client, it can toggle the favorite state or do something else
 * - Can be styled with emotion
 * - Can be sized with height/width
 *
 * @example
 * // The default favorite heart
 * <FavoriteHeartButton
 *    isFavorited={isFavorited}
 *    onClick={() => setIsFavorited(!isFavorited)}
 *    role="button"
 *    type="button"
 *    title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
 *  />
 *
 *
 * @example
 * //  A favorite heart with a lot of padding, a large icon, and a title
 * const StyledFavoriteHeartLink = styled(FavoriteHeartButton)`
 *   padding: 20px;
 * `;
 *
 *  <StyledFavoriteHeartLink
 *    isFavorited={isFavorited}
 *    onClick={() => setIsFavorited(!isFavorited)}
 *    height={60}
 *    width={60}
 *    title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
 *  />
 */
export const FavoriteHeartButton = ({
  isFavorited,
  className: linkClassName,
  height,
  width,
  favoriteColor,
  unfavoriteColor,
  onClick,
  ...props
}: {
  isFavorited: boolean;

  // Allows for emotion styling of FavoriteHeartLink
  className?: string;
  onClick?: React.MouseEventHandler;
} & Omit<
  React.HTMLProps<HTMLAnchorElement> & React.HTMLProps<HTMLButtonElement>,
  'sizes' | 'color' | 'href' | 'role' | 'type' | 'onClick' | 'onMouseEnter' | 'onMouseLeave'
> &
  FavoriteHeartSvgProps &
  Partial<FavoriteHeartColorProps>) => {
  return (
    <FavoriteHeart
      isFavorited={isFavorited}
      height={height}
      width={width}
      favoriteColor={favoriteColor}
      unfavoriteColor={unfavoriteColor}
      component={({ className: favoriteHeartWrapperCss, ...rest }) => (
        <LinkV2
          className={cx(favoriteHeartWrapperCss, linkClassName)}
          arrow={false}
          title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          // onMouseEnter, onMouseLeave, and children props are all passed here
          // long with other props like title, aria-label, etc.
          {...rest}
          {...props}
          role="button"
          type="button"
          onClick={onClick}
        />
      )}
    />
  );
};
